import React, { useEffect, useState, useContext, useRef, useMemo, useCallback } from "react";
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { useWeb3React } from "@web3-react/core";
import { Table } from "@nextui-org/react";
import Web3 from "web3";
import HTTPProvider from "web3";

import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import ContractContext from '../../ContractContext'; //Required for all dApp components using contracts

import { formatDecimals, formatPercentage, formatCurrencyExtended, getTokenPrice, formatWalletAddress, formatBalanceWithIcon, dateTickFormatter } from '../../utils';
import { miniCardHeaderStyle, miniInfoStyle, cardOutlineStyle, cardOutlineExtendedStyle, cardOutlineHeaderStyle, imgGradientCardStyle, cardStyle, infoOutlineStyle, infoOutlineStyleSmall } from "./TileStyles";

import { getGradientCardStyle, childBurnCardStyle } from "./TileStyles";
import burnIcon from "../../imgs/burn_icon.png";
import { AreaChart, Area, BarChart, Rectangle, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

import { Tooltip as PrimeToolTip } from 'primereact/tooltip';

import rocketFiAbi from "../../contracts/rocketFiAbi";

import { ProgressSpinner } from 'primereact/progressspinner';
import { ProgressBar } from 'primereact/progressbar';

import { Progress, Grid } from "@nextui-org/react";

const gridContainerStyle = {
    display: 'grid',
    gap: '10px',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    marginTop: '10px',
    marginBottom: '10px',
};

const containerStyle = {
    maxWidth: '1600px', // Adjust this value according to your preference
    margin: '0 auto', // Centers the container
    width: '100%', // Takes full width on smaller screens
};


const DashboardModuleBurn = ({ data, getData }) => {

    const { library, account } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;
    const isWalletConnected = () => account !== null;
    const rocketFiPrice = getTokenPrice(data.tokensList, "ROCKETFI");

    const LoadingContent = () => <Skeleton width="100%" height="35px" className="mb-2" />;
    const LoadingTitle = () => <Skeleton width="70%" height="25px" className="mb-2" />;

    const burnCardStyle = getGradientCardStyle(burnIcon, 'auto, auto 110%, auto', "rgba(66, 0, 0, 0.94)", "rgba(151, 0, 0, 0.86)");
    const rewardCardStyle = getGradientCardStyle(burnIcon, 'auto, auto 110%, auto', "black", "black");

    const toast = useRef(null);

    const [burnToEarnClicked, setBurnToEarnClicked] = useState(false);
    const [previousWinner, setPreviousWinner] = useState(null);
    const [burnEvents, setBurnEvents] = useState([]);
    const [userBurnEvents, setUserBurnEvents] = useState([]);

    // Initial State for filtering
    const [lastBurnFilter, setLastBurnFilter] = useState(5); // Default to last 5
    const [topBurnFilter, setTopBurnFilter] = useState({ type: 'top', value: 5 }); // Default to top 5
    const [selectedBlock, setSelectedBlock] = useState(null);

    const [lastBlock, setLastBlock] = useState(null);


    const childBurnCardStyleFocused = {
        ...childBurnCardStyle, // inherit all the other styles from childBurnCardStyle
        justifyContent: 'center', // Center the content
        display: 'flex', // Use flex to align items
        flexDirection: 'column', // Stack children vertically
        alignItems: 'center', // Center-align the children
        textAlign: 'center', // Center the text
    };


    const handleBurnToEarn = async () => {
        if (!isWalletConnected()) {
            console.error("Please connect to a wallet before interacting with the contract.");
            return;
        }
        if (!contracts.rocketFi) {
            console.error("RocketFi contract not available.");
            return;
        }

        console.log("RocketFi: Burn Play button clicked");

        try {
            const transaction = contracts.rocketFi.methods.burnForDefaultPool(0).send({ from: account });

            transaction.on("transactionHash", (hash) => {
                toast.current.show({ severity: 'info', summary: 'Transaction Submitted', detail: `Transaction Hash: ${hash}. Follow the transaction on BscScan here: https://bscscan.com/tx/${hash}`, life: 2500 });
            });

            const tx = await transaction;

            toast.current.show({ severity: 'success', summary: 'Transaction Success', detail: 'The transaction has been completed successfully.', life: 2500 });
            setBurnToEarnClicked(true);
        } catch (error) {
            console.error("Error interacting with the RocketFi contract:", error);
            toast.current.show({ severity: 'error', summary: 'Transaction Error', detail: `Error interacting with the RocketFi contract: ${error.message}`, life: 2500 });
        }
        getData();
    };



    // Initialize an empty object to hold the unique sender data
    const [topBurners, setTopBurners] = useState({});
    const [smartCodeData, setSmartCodeData] = useState({});

    // Convert the object to an array and sort it
    // const sortedBurners = Object.keys(topBurners).map(sender => ({
    //     sender,
    //     amountBurned: topBurners[sender]
    // })).sort((a, b) => b.amountBurned - a.amountBurned).slice(0, 5);


    const getSmartCodeData = useCallback(async (address) => {
        const result = await contracts.smartCodes.methods.smartCodeData(address).call();
        //console.log("getSmartCodeData:", result);
        return result;
    }, [contracts.smartCodes]);



    //useEffects
    useEffect(() => {
        // Check if data is loaded and if so update previous winner
        if (data && data.contractData && data.contractData.playData) {
            setPreviousWinner(data.contractData.playData.lastBurnBoostWinner);
        }
    }, [data]);

    useEffect(() => {
        if (!burnEvents) return;
        getBlockTimestamp(burnEvents[0]?.blockNumber).then(setLastBlock);
    }, [burnEvents]);


    useEffect(() => {
        if (burnToEarnClicked && data && data.contractData && data.contractData.playData) {
            if (account && data.contractData.playData.lastBurnBoostWinner === account
                && data.contractData.playData.lastBurnBoostWinner !== previousWinner) { // Check if the previous winner is not the current account
                const wonAmount = formatDecimals(data.contractData.playData.lastBurnBoostWonAmount, 9);
                toast.current.show({
                    severity: 'success',
                    summary: 'Congrats!',
                    detail: `You earned ${wonAmount} Burn Rewards! 🎉`,
                    life: 5000
                });
            } else {
                toast.current.show({
                    severity: 'info',
                    summary: 'Oh Snap!',
                    detail: 'No Burn Rewards Claimed! Try Again 🔥',
                    life: 5000
                });
            }
            setBurnToEarnClicked(false);
        }
    }, [data, account, burnToEarnClicked, previousWinner]);

    useEffect(() => {

        let _topBurners = {};
        // Loop through each event to populate topBurners
        burnEvents.forEach((event) => {
            const sender = event.returnValues?.sender;
            const amountBurned = parseFloat(event.returnValues?.amountBurned);

            if (_topBurners[sender]) {
                _topBurners[sender] += amountBurned;
            } else {
                _topBurners[sender] = amountBurned;
            }
        });

        // Convert the object to an array
        const topBurnersArray = Object.keys(_topBurners).map(sender => ({
            sender: sender,
            burnedAmount: _topBurners[sender]
        }));

        //console.log("Top Burners Array: ", topBurnersArray);

        // Sort the array by burnedAmount
        const sortedTopBurners = topBurnersArray.sort((a, b) => b.burnedAmount - a.burnedAmount);

        // Now, setTopBurners would be an array of objects
        setTopBurners(sortedTopBurners);

    }, [burnEvents]);

    useEffect(() => {
        if (!burnEvents) return;
        if (!contracts.smartCodes) return;

        let uniqueAddresses = [];
        burnEvents.forEach((event) => {
            if (!uniqueAddresses.includes(event?.returnValues?.sender)) {
                uniqueAddresses.push(event?.returnValues?.sender);
            }
        });

        // Use Promise.all to wait for all asynchronous calls to complete before setting the state
        const fetchSmartCodes = async () => {
            let _smartCodeData = {};
            await Promise.all(uniqueAddresses.map(async (address) => {
                const data = await getSmartCodeData(address);
                _smartCodeData[address] = data;
            }));
            setSmartCodeData(_smartCodeData);
        };

        fetchSmartCodes();
    }, [burnEvents, contracts.smartCodes]);

    useEffect(() => {
        //console.log("smartCodeData: ", smartCodeData);
    }, [smartCodeData]);

    const getBlockTime = (blockNumber) => {
        if (!library) return;
        const web3 = new Web3(library.provider);
        return web3.eth.getBlock(blockNumber);
    }

    async function getBlockTimestamp(blockNumber) {
        if (!library.provider) return;
        const web3 = new Web3(library.provider);
        const block = await web3.eth.getBlock(blockNumber);
        return block.timestamp;
    }

    async function fetchTimestampsForEvents(events) {
        if (!library) return;
        const promises = events.map(event => getBlockTimestamp(event.blockNumber));
        const timestamps = await Promise.all(promises);
        return events.map((event, index) => ({
            ...event,
            timestamp: timestamps[index]
        }));
    }

    // Fetch timestamps for the last 5 events and store them in a state
    const [eventsWithTimestamps, setEventsWithTimestamps] = useState([]);
    const [userBurnEventsWithTimestamps, setUserBurnEventsWithTimestamps] = useState([]);

    useEffect(() => {
        if (!burnEvents) return;
        const last5Events = burnEvents.slice(-10);
        fetchTimestampsForEvents(last5Events).then(setEventsWithTimestamps);
    }, [burnEvents]);

    useEffect(() => {
        if (!userBurnEvents) return;
        const last5Events = userBurnEvents;
        fetchTimestampsForEvents(last5Events).then(setUserBurnEventsWithTimestamps);
    }, [userBurnEvents]);

    const [burnRateData, setBurnRateData] = useState([]);
    const [userBurnRateData, setUserBurnRateData] = useState([]);

    // Function to calculate burn rate per day
    const calculateBurnRate = useCallback(async (events) => {
        const eventTimestamps = await fetchTimestampsForEvents(events);
        const burnRateByDay = {};

        eventTimestamps.forEach((event) => {
            // Assuming the timestamp is in seconds and needs to be converted to milliseconds
            const date = new Date(event.timestamp * 1000).toDateString();

            if (burnRateByDay[date]) {
                burnRateByDay[date] += parseFloat(event.returnValues.amountBurned) / 10 ** 9;
            } else {
                burnRateByDay[date] = parseFloat(event.returnValues.amountBurned) / 10 ** 9;
            }
        });

        return Object.keys(burnRateByDay).map((date) => ({
            date,
            "RocketFi Burned": burnRateByDay[date],
        }));
    }, []);

    useEffect(() => {
        if (burnEvents.length > 0 && eventsWithTimestamps.length > 0) {
            calculateBurnRate(burnEvents).then((burnRateData) => {
                setBurnRateData(burnRateData);
            });
        }
    }, [burnEvents, eventsWithTimestamps]);

    useEffect(() => {
        if (userBurnEvents.length > 0 && userBurnEventsWithTimestamps.length > 0) {
            calculateBurnRate(userBurnEvents).then((userBurnRateData) => {
                setUserBurnRateData(userBurnRateData);
            });
        }
    }, [userBurnEvents, userBurnEventsWithTimestamps]);

    // useEffect(() => {
    //     if (burnRateData.length > 0) {
    //         console.log("Burn Rate Data: ", burnRateData);
    //     }
    // }, [burnRateData]);

    // Assuming colors is a predefined object with the styles you want to use
    const colors = {
        background: '#ffffff', // Replace with actual color code
        text: '#cccccc',      // Replace with actual color code
        axis: '#e0e0e0',      // Replace with actual color code
        tooltip: '#333333',   // Replace with actual color code
        line: 'orange',      // Replace with actual color code
    };


    const calculateAverageDailyBurn = () => {
        const filteredData = getFilteredBurnRateData();
        const dailyBurnValues = filteredData.map(data => data["RocketFi Burned"]);
        const averageBurn = dailyBurnValues.reduce((a, b) => a + b, 0) / dailyBurnValues.length;
        return { averageDailyBurn: averageBurn };
    };


    const calculateBurnRateChange = () => {
        const filteredData = getFilteredBurnRateData();
        if (filteredData.length < 2) return { burnRateChange: 0 };

        const firstRate = filteredData[0]["RocketFi Burned"];
        const lastRate = filteredData[filteredData.length - 1]["RocketFi Burned"];
        const change = ((lastRate - firstRate) / firstRate) * 100;
        return { burnRateChange: change };
    };


    const calculatePeakBurnRate = () => {
        const filteredData = getFilteredBurnRateData();
        const peakRate = Math.max(...filteredData.map(data => data["RocketFi Burned"]));
        return { peakBurnRate: peakRate };
    };


    const calculateBurnRateVolatility = () => {
        const filteredData = getFilteredBurnRateData();
        const burnRates = filteredData.map(data => data["RocketFi Burned"]);
        const meanBurnRate = burnRates.reduce((a, b) => a + b, 0) / burnRates.length;
        const variance = burnRates.reduce((total, rate) => total + Math.pow(rate - meanBurnRate, 2), 0) / burnRates.length;
        const volatility = Math.sqrt(variance);
        return { burnRateVolatility: volatility };
    };

    const calculateCurrentBurnVelocity = () => {
        const filteredData = getFilteredBurnRateData();
        if (filteredData.length < 2) return { currentBurnVelocity: 0 };

        const mostRecentBurn = filteredData[filteredData.length - 1]["RocketFi Burned"];
        const averageBurn = filteredData
            .slice(0, -1)
            .map(data => data["RocketFi Burned"])
            .reduce((a, b) => a + b, 0) / (filteredData.length - 1);

        const velocity = (mostRecentBurn / averageBurn - 1) * 100;
        return { currentBurnVelocity: velocity };
    };




    const [selectedTimeRange, setSelectedTimeRange] = useState("7d"); // Default to last 7 days
    const getFilteredBurnRateData = useCallback(() => {
        const now = new Date(); // Current date
        let filteredData = [...burnRateData]; // Copy the original data

        switch (selectedTimeRange) {
            case "7d":
                const date7DaysAgo = new Date();
                date7DaysAgo.setDate(now.getDate() - 7); // Set to 7 days ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date7DaysAgo
                );
                break;
            case "30d":
                const date30DaysAgo = new Date();
                date30DaysAgo.setDate(now.getDate() - 30); // Set to 30 days ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date30DaysAgo
                );
                break;
            case "3m":
                const date3MonthsAgo = new Date();
                date3MonthsAgo.setMonth(now.getMonth() - 3); // Set to 3 months ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date3MonthsAgo
                );
                break;
            case "6m":
                const date6MonthsAgo = new Date();
                date6MonthsAgo.setMonth(now.getMonth() - 6); // Set to 6 months ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date6MonthsAgo
                );
                break;
            case "9m":
                const date9MonthsAgo = new Date();
                date9MonthsAgo.setMonth(now.getMonth() - 9); // Set to 9 months ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date9MonthsAgo
                );
                break;
            case "1y":
                const date1YearAgo = new Date();
                date1YearAgo.setFullYear(now.getFullYear() - 1); // Set to 1 year ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date1YearAgo
                );
                break;
            case "all":
                return filteredData;
            default:
                break;
        }
        return filteredData;
    }, [burnRateData, selectedTimeRange]);

    const [isLoading, setIsLoading] = useState(false);

    const handleTimeRangeChange = useCallback((event) => {
        setSelectedTimeRange(event.target.value);
    }, []);


    useEffect(() => {
        if (burnRateData.length > 0)
            setIsLoading(false); // Set loading false when burnRateData changes
    }, [burnRateData]);

    useEffect(() => {
        setIsLoading(true);
        // Generate the storage key based on the selected time range
        const storageKey = `burnModule_burnEvents_${selectedTimeRange}`;

        // Load burnEvents from localStorage
        // const storedBurnEvents = localStorage.getItem(storageKey);
        // if (storedBurnEvents) {
        //     setBurnEvents(JSON.parse(storedBurnEvents));
        // }
        //console.log("Start fetching burn events")
        // Fetch the latest burn events
        GetPastBurnEvents();

    }, [library, contracts.rocketFi, account, selectedTimeRange]);


    async function GetLatestBlockNumber() {
        if (!library || !contracts.rocketFi) return;
        const web3 = new Web3(library.provider);
        const currentBlock = await web3.eth.getBlock("latest");
        //console.log("currentBlock:", currentBlock.number);
        return currentBlock.number
    }

    // Assuming an average block time of ~3 seconds for Binance Smart Chain
    const BLOCK_TIME_IN_SECONDS = 3;
    const BLOCKS_PER_DAY = 24 * 60 * 60 / BLOCK_TIME_IN_SECONDS;

    const getTimeRangeStartingBlock = async (timeRange) => {
        const currentBlock = await GetLatestBlockNumber();
        let blocksToGoBack;

        switch (timeRange) {
            case '7d':
                blocksToGoBack = 7 * BLOCKS_PER_DAY;
                break;
            case '30d':
                blocksToGoBack = 30 * BLOCKS_PER_DAY;
                break;
            case '45d':
                blocksToGoBack = 45 * BLOCKS_PER_DAY;
                break;
            case '3m':
                blocksToGoBack = 90 * BLOCKS_PER_DAY; // 3 months
                break;
            case '6m':
                blocksToGoBack = 180 * BLOCKS_PER_DAY; // 6 months
                break;
            case '9m':
                blocksToGoBack = 270 * BLOCKS_PER_DAY; // 9 months
                break;
            case '1y':
                blocksToGoBack = 365 * BLOCKS_PER_DAY; // 1 year
                break;
            case 'all':
                blocksToGoBack = totalBlocksToFetch; // Assuming totalBlocksToFetch is defined globally
                break;
            default:
                blocksToGoBack = 7 * BLOCKS_PER_DAY; // Default case
        }

        return Math.max(currentBlock - blocksToGoBack, 0);
    };

    let totalBlocksToFetch = 15000000;
    


    const [loadingProgress, setLoadingProgress] = useState(0);

    async function GetPastBurnEvents() {
        if (!library || !contracts.rocketFi) return;

        const web3 = new Web3(new HTTPProvider("https://binance.llamarpc.com/sk_llama_e2e9915b5438c5313ae3d634858d85cb"));
        const abi = rocketFiAbi;
        const contract_address = "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E";
        const contract = new web3.eth.Contract(abi, contract_address);

        const maxBlocks = 10000; // Maximum number of blocks per chunk

        let currentBlock = await GetLatestBlockNumber();
        let fromBlock = await getTimeRangeStartingBlock(selectedTimeRange); // Get fromBlock based on selected time range
        let promises = [];

        // Calculate the number of blocks to fetch based on the selected time range
        let blocksToFetch = Math.min(currentBlock - fromBlock);

        // Determine total chunks and create an array to track each fetch progress
        let totalChunks = Math.ceil(blocksToFetch / maxBlocks);

        // Reset loading progress
        setLoadingProgress(0);
        //console.log(`Total Chunks: ${totalChunks}`);

        // Split the blocks to fetch into chunks and fetch each chunk concurrently
        for (let offset = 0; offset < blocksToFetch; offset += maxBlocks) {
            let chunkFromBlock = Math.max(fromBlock + offset, 0);
            let chunkToBlock = Math.min(fromBlock + offset + maxBlocks, currentBlock);
            
            // Add a promise for each chunk to the promises array
            promises.push(contract.getPastEvents('TokensBurned', { fromBlock: chunkFromBlock, toBlock: chunkToBlock }));
        }

        // Track the completion of each promise
        Promise.all(promises.map((p, index) =>
            p.then((result) => {
                // Update progress as each promise resolves
                setLoadingProgress((prevProgress) => {
                    const progressIncrement = 100 / totalChunks;
                    return prevProgress + progressIncrement;
                });
                //console.log(`Progress: ${loadingProgress}%`);
                //console.log(`Result:`, result);
                return result; // Return the result to be used in the final processing
            })
        )).then((allEventsArray) => {
            // All promises have resolved
            let allEvents = [].concat.apply([], allEventsArray);
            allEvents.sort((a, b) => a.blockNumber - b.blockNumber);
            setBurnEvents(allEvents);
            setIsLoading(false); // Assuming you have an isLoading state
        }).catch((error) => {
            console.error("Error fetching events in batch:", error);
            setIsLoading(false); // Update loading state in case of error
        });
    }


    // Filter the burn events to include only those by the user
    useEffect(() => {
        if (account && burnEvents.length > 0) {
            const filteredEvents = burnEvents.filter(event => event.returnValues.sender === account);
            setUserBurnEvents(filteredEvents);
        }
    }, [account, burnEvents]);

    const getFilteredUserBurnRateData = useCallback(() => {
        const now = new Date(); // Current date
        let filteredData = [...userBurnRateData]; // Copy the original data

        switch (selectedTimeRange) {
            case "7d":
                const date7DaysAgo = new Date();
                date7DaysAgo.setDate(now.getDate() - 7); // Set to 7 days ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date7DaysAgo
                );
                break;
            case "30d":
                const date30DaysAgo = new Date();
                date30DaysAgo.setDate(now.getDate() - 30); // Set to 30 days ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date30DaysAgo
                );
                break;
            case "3m":
                const date3MonthsAgo = new Date();
                date3MonthsAgo.setMonth(now.getMonth() - 3); // Set to 3 months ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date3MonthsAgo
                );
                break;
            case "6m":
                const date6MonthsAgo = new Date();
                date6MonthsAgo.setMonth(now.getMonth() - 6); // Set to 6 months ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date6MonthsAgo
                );
                break;
            case "9m":
                const date9MonthsAgo = new Date();
                date9MonthsAgo.setMonth(now.getMonth() - 9); // Set to 9 months ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date9MonthsAgo
                );
                break;
            case "1y":
                const date1YearAgo = new Date();
                date1YearAgo.setFullYear(now.getFullYear() - 1); // Set to 1 year ago
                filteredData = filteredData.filter(data =>
                    new Date(data.date) >= date1YearAgo
                );
                break;
            case "all":
                return filteredData;
            default:
                break;
        }
        return filteredData;
    }, [userBurnRateData, selectedTimeRange]);


    return (
        <div style={containerStyle}>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
                gap: '10px',
                marginTop: '10px',
                marginBottom: '10px',
            }}>
                <Card title={
                    data?.contractData?.burnData !== undefined
                        ? <div style={infoOutlineStyle}>Burn Zone</div>
                        : <Skeleton width="70%" height="25px" className="mb-2" />
                }
                    style={burnCardStyle}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
                        gap: '10px',
                    }}>
                        <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(115px, 1fr))', gap: '10px', marginTop: '10px' }}>
                                <div>
                                    <div style={miniCardHeaderStyle}>
                                        {data?.contractData?.burnData
                                            ? 'You Have Burned'
                                            : <LoadingTitle />
                                        }
                                    </div>
                                    <div style={miniInfoStyle}>
                                        {data?.contractData?.burnData
                                            ? formatBalanceWithIcon(data?.contractData?.burnData?.burnedAmount, "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E", data?.dbData, 9)
                                            : <LoadingContent />}
                                    </div>
                                    <div style={{ fontSize: "12px" }}>
                                        {data?.contractData?.burnData
                                            ? formatCurrencyExtended((data?.contractData?.burnData?.burnedAmount / (10 ** 9)) * rocketFiPrice)
                                            : <LoadingTitle />}
                                    </div>
                                </div>

                                <div>
                                    <div style={miniCardHeaderStyle}>
                                        {data?.contractData?.tokenData
                                            ? 'Total Burned'
                                            : <LoadingTitle />
                                        }
                                    </div>
                                    <div style={miniInfoStyle}>
                                        {data?.contractData?.tokenData
                                            ? formatBalanceWithIcon(data?.contractData?.tokenData?.rocketFiDestroyedByBurn, "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E", data?.dbData, 9)
                                            : <LoadingContent />}
                                    </div>
                                    <div style={{ fontSize: "12px" }}>
                                        {data?.contractData?.tokenData
                                            ? formatCurrencyExtended((data?.contractData?.tokenData?.rocketFiDestroyedByBurn / (10 ** 9)) * rocketFiPrice)
                                            : <LoadingTitle />}
                                    </div>
                                </div>


                                <div>
                                    <div style={miniCardHeaderStyle}>
                                        {data?.contractData?.tokenData
                                            ? 'Supply Burned'
                                            : <LoadingTitle />
                                        }
                                    </div>
                                    <div style={miniInfoStyle}>
                                        {data?.contractData?.burnData
                                            ? formatPercentage(parseFloat(data?.contractData?.tokenData?.rocketFiDestroyedByBurn)
                                                / (parseFloat(data?.contractData?.tokenData?.rocketFiTotalSupply) + parseFloat(data?.contractData?.tokenData?.rocketFiDestroyedByBurn)))
                                            : <LoadingContent />}
                                    </div>
                                </div>





                            </div>


                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px', marginTop: '20px' }}>
                                <Card
                                    title={
                                        data?.contractData?.burnData !== undefined
                                            ? <div style={cardOutlineHeaderStyle}>Burn Play</div>
                                            : <Skeleton width="70%" height="25px" className="mb-2" />
                                    }
                                    style={cardStyle}>

                                    <div style={{ marginTop: '20px' }}>




                                        <Card title={
                                            data?.contractData?.burnData !== undefined
                                                ? <div style={{ fontSize: '16px', }}>Burn Rewards Available</div>
                                                : <Skeleton width="70%" height="25px" className="mb-2" />
                                        }
                                            style={rewardCardStyle}>

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ ...miniInfoStyle, fontSize: '24px' }}>
                                                        {data?.contractData
                                                            ? formatBalanceWithIcon(data?.contractData?.playData?.burnBoostAvailable, "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E", data?.dbData, 9)
                                                            : <LoadingContent />}
                                                    </div>
                                                    <div style={{ fontSize: "12px" }}>
                                                        {data?.contractData?.playData
                                                            ? formatCurrencyExtended((data?.contractData?.playData?.burnBoostAvailable / (10 ** 9)) * rocketFiPrice)
                                                            : <LoadingTitle />}
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <Button
                                                    rounded
                                                    severity="danger"
                                                    onClick={handleBurnToEarn}
                                                    size="small"
                                                    style={{
                                                        justifyContent: 'center',
                                                        marginTop: '10px',
                                                        alignSelf: 'center',
                                                        width: '200px',
                                                        justifyContent: 'center',
                                                        marginLeft: '10px',
                                                        marginRight: '10px',
                                                    }}
                                                >
                                                    Burn Play
                                                </Button>
                                            </div>
                                        </Card>

                                        <Card title={
                                            data?.contractData?.burnData !== undefined
                                                ? <div style={{ fontSize: '16px', }}>Burn Play Info</div>
                                                : <Skeleton width="70%" height="25px" className="mb-2" />
                                        }
                                            style={rewardCardStyle}>

                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(115px, 1fr))', gap: '10px', marginTop: '10px' }}>

                                                <div>
                                                    <div style={miniCardHeaderStyle}>
                                                        {data?.contractData
                                                            ? 'Reward Chance'
                                                            : <LoadingTitle />
                                                        }
                                                    </div>
                                                    {data?.contractData?.playData?.rocketTokens > 0 ?
                                                        <div style={miniInfoStyle}>
                                                            {data?.contractData
                                                                ? <span>{formatPercentage(parseFloat(data?.contractData?.playData?.yourPoolChanceBurn) / 10000)} {
                                                                    <div style={{
                                                                        fontWeight: 'normal', fontSize: '12px'
                                                                    }}>
                                                                        {'('}2x Applied{')'}
                                                                    </div>}</span>
                                                                : <LoadingContent />}
                                                        </div>
                                                        :
                                                        <div style={miniInfoStyle}>
                                                            {data?.contractData
                                                                ? formatPercentage(parseFloat(data?.contractData?.playData?.yourPoolChanceBurn) / 10000)
                                                                : <LoadingContent />}
                                                        </div>
                                                    }
                                                </div>
                                                <div>
                                                    <div style={miniCardHeaderStyle}>
                                                        {data?.contractData
                                                            ? '2x Multipliers'
                                                            : <LoadingTitle />
                                                        }
                                                    </div>
                                                    <div style={miniInfoStyle}>
                                                        {data?.contractData
                                                            ? data?.contractData?.playData?.rocketTokens
                                                            : <LoadingContent />}
                                                    </div>
                                                </div>

                                                <div>
                                                    <div style={miniCardHeaderStyle}>
                                                        {data?.contractData
                                                            ? 'Tokens Required per Burn Play'
                                                            : <LoadingTitle />
                                                        }
                                                    </div>
                                                    <div style={miniInfoStyle}>
                                                        {data?.contractData
                                                            ? formatBalanceWithIcon(data?.contractData?.playData?.rocketFiRequiredForRollingBurnBoost, "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E", data?.dbData, 9)
                                                            : <LoadingContent />}
                                                    </div>
                                                    <div style={{ fontSize: "12px" }}>
                                                        {data?.contractData?.playData
                                                            ? formatCurrencyExtended((data?.contractData?.playData?.rocketFiRequiredForRollingBurnBoost / (10 ** 9)) * rocketFiPrice)
                                                            : <LoadingTitle />}
                                                    </div>
                                                </div>

                                            </div>
                                        </Card>
                                        <Card title={
                                            data?.contractData?.burnData !== undefined
                                                ? <div style={{ fontSize: '16px', }}>Previously Rewarded</div>
                                                : <Skeleton width="70%" height="25px" className="mb-2" />
                                        }
                                            style={rewardCardStyle}>

                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(115px, 1fr))', gap: '10px', marginTop: '10px' }}>

                                                <div>
                                                    <div style={miniCardHeaderStyle}>
                                                        {data?.contractData
                                                            ? 'Last Burn Reward Amount'
                                                            : <LoadingTitle />
                                                        }
                                                    </div>
                                                    <div style={miniInfoStyle}>
                                                        {data?.contractData
                                                            ? formatBalanceWithIcon(data?.contractData?.playData?.lastBurnBoostWonAmount, "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E", data?.dbData, 9)
                                                            : <LoadingContent />}
                                                    </div>
                                                    <div style={{ fontSize: "12px" }}>
                                                        {data?.contractData?.playData?.lastBurnBoostWonAmount
                                                            ? formatCurrencyExtended((data?.contractData?.playData?.lastBurnBoostWonAmount / (10 ** 9)) * rocketFiPrice)
                                                            : <LoadingTitle />}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div style={miniCardHeaderStyle}>
                                                        {data?.contractData
                                                            ? 'Last Wallet Rewarded'
                                                            : <LoadingTitle />
                                                        }
                                                    </div>
                                                    <div style={miniInfoStyle}>
                                                        {data?.contractData?.playData?.lastBurnBoostWinner
                                                            ? formatWalletAddress(data?.contractData?.playData?.lastBurnBoostWinner)
                                                            : <LoadingContent />}
                                                    </div>
                                                </div>



                                            </div>
                                        </Card>

                                        {userBurnEvents.length > 0 && <div >
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))', gap: '10px', marginTop: '10px' }}>
                                                <div
                                                    style={{
                                                        padding: '20px', borderRadius: '20px', backgroundColor: 'var(--surface-ground)'
                                                    }}
                                                >
                                                    <div style={{
                                                        ...infoOutlineStyle, fontSize: '16px',
                                                    }}>Your RocketFi Burned</div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginTop: '10px' }}>

                                                        <ResponsiveContainer width="100%" height={150}>
                                                            {isLoading ? (
                                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                                    {/* <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" /> */}
                                                                    <Progress shadow  size="sm"  value={loadingProgress} color="primary" status="primary" style={{maxWidth: '50%'}}/>
                                                                </div>
                                                            ) : (
                                                                <BarChart
                                                                    data={getFilteredUserBurnRateData()}
                                                                    margin={{ top: 25, right: 5, left: -25, bottom: 5 }}
                                                                >
                                                                    <XAxis
                                                                        dataKey="date"
                                                                        tickFormatter={dateTickFormatter}
                                                                        stroke={colors.text}
                                                                        axisLine={{ stroke: colors.axis }}
                                                                        tickLine={{ stroke: colors.axis }}
                                                                        tick={{ fontSize: 12 }}
                                                                    />
                                                                    <YAxis
                                                                        name="Burn Rate"
                                                                        tickFormatter={(value) => formatDecimals(value, 0)}
                                                                        stroke={colors.text}
                                                                        axisLine={{ stroke: colors.axis }}
                                                                        tickLine={{ stroke: colors.axis }}
                                                                        tick={{ fontSize: 12 }}
                                                                    />
                                                                    <Tooltip
                                                                        formatter={(value) => formatDecimals(value, 0)}
                                                                        wrapperStyle={{ background: colors.tooltip }}
                                                                        contentStyle={{ borderColor: colors.text, backgroundColor: '#000000' }}
                                                                        labelStyle={{ color: colors.text }}
                                                                        itemStyle={{ color: colors.text }}
                                                                    />
                                                                    <Legend
                                                                        formatter={(value) => 'RocketFi Burned'}
                                                                        wrapperStyle={{ color: colors.text }}
                                                                    />
                                                                    <Bar
                                                                        type="monotone"
                                                                        dataKey="RocketFi Burned"
                                                                        stroke={colors.line}
                                                                        fill={colors.line}

                                                                    />

                                                                </BarChart>
                                                            )}
                                                        </ResponsiveContainer>
                                                    </div>
                                                </div>
                                                <div
                                                    style={{
                                                        padding: '20px', borderRadius: '20px', backgroundColor: 'var(--surface-ground)'
                                                    }}
                                                >
                                                    {userBurnEventsWithTimestamps.length > 0 && <div>
                                                        <div style={{
                                                            ...miniInfoStyle, marginBottom: '25px',
                                                            textAlign: 'center', fontSize: '16px', fontWeight: 'bold', marginTop: '15px'
                                                        }}>
                                                            Your Burn History
                                                        </div>
                                                        <Table aria-label="Your Burn Events" css={{ height: "auto", minWidth: "100%", backgroundColor: "white" }}>
                                                            <Table.Header>
                                                                <Table.Column>Burned</Table.Column>
                                                                <Table.Column>Time</Table.Column>
                                                            </Table.Header>
                                                            <Table.Body>
                                                                {userBurnEventsWithTimestamps
                                                                    .sort((a, b) => b.timestamp - a.timestamp)
                                                                    .map((event, index) => (
                                                                        <Table.Row key={index}>
                                                                            <Table.Cell>
                                                                                <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                                                                                    {formatDecimals(event.returnValues.amountBurned, 9)} /
                                                                                    <em>{formatCurrencyExtended((event.returnValues.amountBurned / (10 ** 9)) * rocketFiPrice)}</em>
                                                                                </div>
                                                                            </Table.Cell>
                                                                            <Table.Cell>
                                                                                <div style={{ fontSize: "12px", fontWeight: "normal" }}>
                                                                                    {new Date(event.timestamp * 1000).toLocaleString()}
                                                                                </div>
                                                                            </Table.Cell>
                                                                        </Table.Row>
                                                                    ))}
                                                            </Table.Body>
                                                            {userBurnEventsWithTimestamps.length > 5 && <Table.Pagination
                                                                shadow
                                                                noMargin
                                                                align="center"
                                                                rowsPerPage={5}
                                                                onPageChange={(page) => console.log({ page })}
                                                            />}
                                                        </Table>

                                                    </div>}
                                                </div>
                                            </div>
                                        </div>}
                                    </div>

                                </Card>


                                <Card
                                    title={
                                        <div style={{
                                            ...infoOutlineStyle, fontSize: '16px',
                                        }}>Daily RocketFi Burned</div>
                                    }
                                    style={cardStyle}
                                >

                                    <select value={selectedTimeRange} onChange={handleTimeRangeChange}
                                        style={{
                                            borderRadius: '20px',
                                            padding: '5px',
                                            textColor: 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        <option value="7d">Last 7 days</option>
                                        <option value="30d">Last 30 days</option>
                                        <option value="3m">Last 3 months</option>
                                        <option value="6m">Last 6 months</option>
                                        <option value="9m">Last 9 months</option>
                                        <option value="1y">Last 1 year</option>
                                        <option value="all">All</option>
                                    </select>

                                    {/* <Button className="p-button-rounded p-button-danger p-button-outlined" onClick={clearLocalStorageData} aria-label="Clear Data">Clear Cache</Button> */}


                                    <ResponsiveContainer width="100%" height={150}>
                                        {isLoading ? (
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                  <Progress shadow  size="sm" value={loadingProgress} color="primary" status="primary" style={{maxWidth: '50%'}}/>
                                                {/* <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" /> */}
{/*                                                
                                                <Progress value={loadingProgress} shadow color="primary" status="primary" />
                                      
                                                <Progress value={loadingProgress} shadow color="primary" status="primary" /> */}
                                                {/* <div style={{ position: "fixed", top: 0, left: 0, width: "100%", backgroundColor: "rgba(0,0,0,0.5)", color: "#fff", textAlign: "center" }}>
                                                    <Progress value={loadingProgress} shadow color="primary" status="primary" />
                                                </div> */}

                                            </div>
                                        ) : (
                                            <AreaChart
                                                data={getFilteredBurnRateData()}
                                                margin={{
                                                    top: 25, right: 5, left: -25, bottom: 5,
                                                }}
                                            >

                                                <XAxis
                                                    dataKey="date"
                                                    tickFormatter={dateTickFormatter}
                                                    stroke={colors.text}
                                                    axisLine={{ stroke: colors.axis }}
                                                    tickLine={{ stroke: colors.axis }}
                                                    tick={{ fontSize: 12 }}
                                                />
                                                <YAxis
                                                    name="Burn Rate"
                                                    tickFormatter={(value) => formatDecimals(value, 0)}
                                                    stroke={colors.text}
                                                    axisLine={{ stroke: colors.axis }}
                                                    tickLine={{ stroke: colors.axis }}
                                                    tick={{ fontSize: 12 }}
                                                />
                                                <Tooltip
                                                    formatter={(value) => formatDecimals(value, 0)}
                                                    wrapperStyle={{ background: colors.tooltip }}
                                                    contentStyle={{ borderColor: colors.text, backgroundColor: '#000000' }}
                                                    labelStyle={{ color: colors.text }}
                                                    itemStyle={{ color: colors.text }}
                                                />
                                                <Legend
                                                    formatter={(value) => 'Total Burned'}
                                                    wrapperStyle={{ color: colors.text }}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="RocketFi Burned"
                                                    stroke={colors.line}
                                                    fill={colors.line}
                                                    activeDot={{ r: 8 }}
                                                />
                                            </AreaChart>
                                        )}
                                    </ResponsiveContainer>
                                   {burnRateData.length > 1  && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(115px, 1fr))', gap: '10px', marginTop: '10px' }}>


                                        <div>
                                            <div style={miniCardHeaderStyle}>
                                                <PrimeToolTip target=".average-daily-burn-tooltip" style={{ maxWidth: "225px" }} />
                                                <div className="average-daily-burn-tooltip" data-pr-tooltip="This number is calculated by taking the total RocketFi burned each day and finding the mean value over the period.">
                                                    <div style={miniCardHeaderStyle}>Average Daily Burn</div>
                                                    <div style={miniInfoStyle}>
                                                        {burnRateData && burnRateData.length > 1 ? formatBalanceWithIcon(calculateAverageDailyBurn().averageDailyBurn, "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E", data?.dbData, 0) : <LoadingContent />}


                                                    </div>
                                                </div>
                                            </div>
                                        </div>


                                        <div>
                                            <div style={miniCardHeaderStyle}>
                                                <PrimeToolTip target=".burn-rate-change-tooltip" style={{ maxWidth: "225px" }} />
                                                <div className="burn-rate-change-tooltip" data-pr-tooltip="This percentage shows how much the burn rate has increased or decreased over the observed period.">
                                                    <div style={miniCardHeaderStyle}>Burn Rate Change</div>
                                                    <div style={miniInfoStyle}>
                                                        {burnRateData && burnRateData.length > 1 ? formatPercentage(calculateBurnRateChange().burnRateChange) : <LoadingContent />}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <div style={miniCardHeaderStyle}>
                                                <PrimeToolTip target=".peak-burn-rate-tooltip" style={{ maxWidth: "225px" }} />
                                                <div className="peak-burn-rate-tooltip" data-pr-tooltip="This is the maximum amount of RocketFi burned on any recorded day.">
                                                    <div style={miniCardHeaderStyle}>Peak Burn Rate</div>
                                                    <div style={miniInfoStyle}>
                                                        {burnRateData && burnRateData.length > 1 ? formatBalanceWithIcon(calculatePeakBurnRate().peakBurnRate, "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E", data?.dbData, 0) : <LoadingContent />}


                                                    </div>
                                                </div>
                                            </div>
                                        </div>


                                        <div>
                                            <div style={miniCardHeaderStyle}>
                                                <PrimeToolTip target=".current-burn-velocity-tooltip" style={{ maxWidth: "225px" }} />
                                                <div className="current-burn-velocity-tooltip" data-pr-tooltip="This number represents the rate of change in the amount of RocketFi burned from the most recent day compared to the average of the preceding period.">
                                                    <div style={miniCardHeaderStyle}>Current Burn Velocity</div>
                                                    <div style={miniInfoStyle}>
                                                        {burnRateData && burnRateData.length > 1 ? formatPercentage(calculateCurrentBurnVelocity().currentBurnVelocity) : <LoadingContent />}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>


                                        <div>
                                            <div style={miniCardHeaderStyle}>
                                                <PrimeToolTip target=".burn-rate-volatility-tooltip" style={{ maxWidth: "225px" }} />
                                                <div className="burn-rate-volatility-tooltip" data-pr-tooltip="Volatility measures the frequency of changes in the burn rate. High volatility means the burn rate varies greatly, often driving scarcity and potentially more value. It's a sign of strong token dynamics at work.">
                                                    <div style={miniCardHeaderStyle}>Burn Rate Volatility</div>
                                                    <div style={miniInfoStyle}>
                                                        {burnRateData && burnRateData.length > 1 ? formatBalanceWithIcon(calculateBurnRateVolatility().burnRateVolatility, "0x6e61579c22F9a6dA63a33e819f29B6697d2a126E", data?.dbData, 0) : <LoadingContent />}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '10px' }} />
                                    </div>}

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginTop: '10px' }}>


                                        {eventsWithTimestamps.length > 0 && <div>
                                            <div style={{
                                                ...miniInfoStyle, marginBottom: '25px',
                                                textAlign: 'center', fontSize: '16px', fontWeight: 'bold', marginTop: '15px'
                                            }}>
                                                Last 10 Burns

                                            </div>
                                            <Table
                                                aria-label="Last 5 Burn Events"
                                                css={{
                                                    height: "auto",
                                                    minWidth: "100%",
                                                    backgroundColor: "white",
                                                }}>


                                                <Table.Header>
                                                    <Table.Column>Address/SmartCode</Table.Column>
                                                    <Table.Column>Burned</Table.Column>
                                                    <Table.Column>Time</Table.Column>
                                                </Table.Header>
                                                <Table.Body>

                                                    {eventsWithTimestamps
                                                        .sort((a, b) => b.blockNumber - a.blockNumber)
                                                        .slice(0, 10)
                                                        .map((event, index) => (

                                                            <Table.Row key={index}>

                                                                <Table.Cell><div style={{
                                                                    fontSize: "12px",
                                                                    fontWeight: "normal",
                                                                }}>{smartCodeData[event.returnValues.sender]?.smartCode !== '' ? smartCodeData[event.returnValues.sender]?.smartCode : formatWalletAddress(event.returnValues.sender)}</div></Table.Cell>
                                                                <Table.Cell><div style={{
                                                                    fontSize: "12px",
                                                                    fontWeight: "normal",
                                                                }}>{formatDecimals(event.returnValues.amountBurned, 9)} / <em>{formatCurrencyExtended((event.returnValues.amountBurned / (10 ** 9)) * rocketFiPrice)}</em></div></Table.Cell>
                                                                <Table.Cell><div style={{
                                                                    fontSize: "12px",
                                                                    fontWeight: "normal",
                                                                }}>{new Date(event.timestamp * 1000).toLocaleString()}</div></Table.Cell>
                                                            </Table.Row>

                                                        ))}

                                                </Table.Body>
                                            </Table></div>}


                                        {burnEvents.length > 0 && topBurners && Array.isArray(topBurners) && (
                                            <div>
                                                <div style={{
                                                    ...miniInfoStyle, marginBottom: '10px',
                                                    textAlign: 'center', fontSize: '16px', fontWeight: 'bold', marginTop: '15px'
                                                }}>
                                                    Top Burners
                                                    {burnEvents && burnEvents.length > 0 && (
                                                        <a href={`https://bscscan.com/tx/${burnEvents[0].transactionHash}`}
                                                            target="_blank" rel="noopener noreferrer"
                                                            style={{ textDecoration: 'none', color: 'inherit' }}>
                                                            <div style={{ fontSize: '12px', fontWeight: 'normal', textDecoration: 'underline', marginTop: '5px' }}>
                                                                Since {new Date(lastBlock * 1000).toLocaleString()}
                                                            </div>
                                                        </a>
                                                    )}
                                                </div>

                                                <Table
                                                    aria-label="Top 10 Burners"
                                                    css={{
                                                        height: "auto",
                                                        minWidth: "100%",
                                                        backgroundColor: "white",
                                                    }}>
                                                    <Table.Header>
                                                        <Table.Column>Address/SmartCode</Table.Column>
                                                        <Table.Column>Burned</Table.Column>
                                                    </Table.Header>
                                                    <Table.Body>
                                                        {topBurners.map((burner, index) => (
                                                            <Table.Row key={index}>
                                                                <Table.Cell><div style={{
                                                                    fontSize: "12px",
                                                                    fontWeight: "normal",
                                                                }}>{smartCodeData[burner.sender]?.smartCode !== '' ? smartCodeData[burner.sender]?.smartCode : formatWalletAddress(burner.sender)}</div></Table.Cell>
                                                                <Table.Cell><div style={{
                                                                    fontSize: "12px",
                                                                    fontWeight: "normal",
                                                                }}>{formatDecimals(burner.burnedAmount, 9)} / <em>{formatCurrencyExtended((burner.burnedAmount / (10 ** 9)) * rocketFiPrice)}</em></div></Table.Cell>
                                                            </Table.Row>

                                                        ))}
                                                    </Table.Body>
                                                    {topBurners.length > 10 && <Table.Pagination
                                                        shadow
                                                        noMargin
                                                        align="center"
                                                        rowsPerPage={10}
                                                        onPageChange={(page) => console.log({ page })}
                                                    />}
                                                </Table>
                                            </div>
                                        )}



                                    </div>

                                </Card>



                            </div>
                        </div>



                    </div>
                    <Toast ref={toast} />
                </Card>
            </div>
        </div>
    );
}

//export default React.memo(DashboardModuleBurn);
export default React.memo(DashboardModuleBurn);
